/**
 * Run Evaluation Modal
 *
 * Modal for configuring and running evaluations against datasets
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  FiX,
  FiPlay,
  FiDatabase,
  FiFileText,
  FiCpu,
  FiShield,
  FiAlertTriangle,
  FiKey,
  FiHelpCircle,
  FiExternalLink,
  FiClock,
} from 'react-icons/fi';
import {
  getDatasets,
  getPrompts,
  runEvaluation,
  getJudgeRubrics,
  getCredentials,
  getDatasetVariables,
  getAvailableModels,
  RunEvaluationParams,
} from '../../../../services/aiOperationsApi';
import { SCORING_METHODS, BEDROCK_TASK_TYPES, BEDROCK_EVALUATION_METRICS } from './ScoringMethodSelector';
import { SAFETY_FAIL_THRESHOLDS } from './SafetyTestingPanel';

interface Dataset {
  id: string;
  name: string;
  content_type?: string;
  item_count?: number;
  test_case_count?: number;
}

interface Prompt {
  id: string;
  name: string;
  version: string | number;
  template?: string;
}

interface Rubric {
  id: string;
  name: string;
}

interface Credential {
  id: string;
  name: string;
  provider: string;
  is_enabled: boolean;
}

interface ModelEntry {
  id: string;
  name: string;
  provider: string;
}

interface VariableWarning {
  missing: string[];
  extra: string[];
}

interface RunEvaluationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (result: unknown) => void;
  initialDatasetId?: string | null;
}

const RunEvaluationModal: React.FC<RunEvaluationModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  initialDatasetId = null,
}) => {
  // Form state
  const [datasetId, setDatasetId] = useState(initialDatasetId ?? '');
  const [promptId, setPromptId] = useState('');
  const [credentialId, setCredentialId] = useState('');
  const [modelId, setModelId] = useState('');
  const [scoringMethod, setScoringMethod] = useState('contains');
  const [passThreshold, setPassThreshold] = useState(0.7);

  // Advanced options
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [includeSafety, setIncludeSafety] = useState(false);
  const [safetyFailThreshold, setSafetyFailThreshold] = useState('high');
  const [runAssertions, setRunAssertions] = useState(true);

  // LLM Judge options
  const [judgeRubricId, setJudgeRubricId] = useState('');

  // Semantic similarity options
  const [similarityThreshold, setSimilarityThreshold] = useState(0.8);

  // AWS Bedrock Evaluation options
  const [bedrockTaskType, setBedrockTaskType] = useState('QuestionAndAnswer');
  const [bedrockMetrics, setBedrockMetrics] = useState<string[]>(['Builtin.Accuracy']);
  const [bedrockTimeoutMinutes, setBedrockTimeoutMinutes] = useState(60);

  // Secondary metrics
  const [calculateSecondaryMetrics, setCalculateSecondaryMetrics] = useState(false);
  const [secondaryMetrics, setSecondaryMetrics] = useState<string[]>([]);

  // Data loading
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [rubrics, setRubrics] = useState<Rubric[]>([]);
  const [credentials, setCredentials] = useState<Credential[]>([]);
  const [allModels, setAllModels] = useState<ModelEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Variable validation state
  const [datasetVariables, setDatasetVariables] = useState<string[]>([]);
  const [promptVariables, setPromptVariables] = useState<string[]>([]);
  const [variableWarning, setVariableWarning] = useState<VariableWarning | null>(null);

  // Load datasets, prompts, rubrics, and credentials on mount
  useEffect(() => {
    if (isOpen) {
      if (initialDatasetId) setDatasetId(initialDatasetId);
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialDatasetId]);

  // Fetch dataset variables when dataset changes
  useEffect(() => {
    const fetchDatasetVars = async () => {
      if (!datasetId) {
        setDatasetVariables([]);
        return;
      }
      try {
        const result = await getDatasetVariables(datasetId);
        setDatasetVariables(result.variables || []);
      } catch (err) {
        console.error('Error fetching dataset variables:', err);
        setDatasetVariables([]);
      }
    };
    fetchDatasetVars();
  }, [datasetId]);

  // Extract prompt variables when prompt changes
  useEffect(() => {
    if (!promptId) {
      setPromptVariables([]);
      return;
    }
    const selectedPrompt = prompts.find((p) => p.id === promptId);
    if (selectedPrompt?.template) {
      const matches = selectedPrompt.template.match(/\{\{(\w+)\}\}/g) || [];
      const vars = matches.map((m) => m.replaceAll(/\{\{|\}\}/g, ''));
      setPromptVariables(Array.from(new Set(vars)));
    } else {
      setPromptVariables([]);
    }
  }, [promptId, prompts]);

  // Compare prompt variables with dataset variables
  useEffect(() => {
    if (!promptId || promptVariables.length === 0) {
      setVariableWarning(null);
      return;
    }

    const missingInDataset = promptVariables.filter((v) => !datasetVariables.includes(v));
    const extraInDataset = datasetVariables.filter((v) => !promptVariables.includes(v));

    if (missingInDataset.length > 0 || extraInDataset.length > 0) {
      setVariableWarning({ missing: missingInDataset, extra: extraInDataset });
    } else {
      setVariableWarning(null);
    }
  }, [promptId, promptVariables, datasetVariables]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [datasetsRes, promptsRes, credentialsRes, modelsRes] = await Promise.all([
        getDatasets({ limit: 100 }),
        getPrompts({ limit: 100, status: 'active' }),
        getCredentials(),
        getAvailableModels(),
      ]);

      let rubricsRes: unknown = [];
      try {
        rubricsRes = await getJudgeRubrics({ limit: 100 });
      } catch (rubricErr) {
      }

      setDatasets(datasetsRes.data || datasetsRes.items || datasetsRes || []);
      setPrompts(promptsRes.data || promptsRes.items || promptsRes || []);
      const rubricsAny = rubricsRes as { items?: Rubric[]; data?: Rubric[] } | Rubric[];
      const resolvedRubrics = Array.isArray(rubricsAny)
        ? rubricsAny
        : rubricsAny.items || rubricsAny.data || [];
      setRubrics(resolvedRubrics);

      const models: ModelEntry[] = modelsRes?.models || modelsRes || [];
      setAllModels(models);

      const enabledCredentials: Credential[] = (credentialsRes || []).filter(
        (c: Credential) => c.is_enabled
      );
      setCredentials(enabledCredentials);

      if (enabledCredentials.length > 0 && !credentialId) {
        const firstCred = enabledCredentials[0];
        setCredentialId(firstCred.id);
        const providerModels = models.filter((m) => m.provider === firstCred.provider);
        if (providerModels.length > 0) {
          setModelId(providerModels[0].id);
        }
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCredentialChange = (newCredentialId: string) => {
    setCredentialId(newCredentialId);
    const cred = credentials.find((c) => c.id === newCredentialId);
    if (cred) {
      const providerModels = allModels.filter((m) => m.provider === cred.provider);
      if (providerModels.length > 0) {
        setModelId(providerModels[0].id);
      } else {
        setModelId('');
      }
    }
  };

  const currentCredential = credentials.find((c) => c.id === credentialId);
  const availableModels = useMemo(
    () => currentCredential
      ? allModels.filter((m) => m.provider === currentCredential.provider)
      : [],
    [currentCredential, allModels]
  );

  const filteredModels = useMemo(() => {
    if (scoringMethod !== 'bedrock_evaluation') {
      return availableModels;
    }
    return availableModels.filter((m) => !m.id.startsWith('global.'));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableModels, scoringMethod]);

  const hasAwsCredentials = useMemo(() => {
    return credentials.some((c) => c.provider === 'bedrock' && c.is_enabled);
  }, [credentials]);

  const availableScoringMethods = useMemo(() => {
    return SCORING_METHODS.filter((method) => !method.requiresAws || hasAwsCredentials);
  }, [hasAwsCredentials]);

  // Auto-reset modelId when switching to Bedrock Evaluation if current model is incompatible
  useEffect(() => {
    if (scoringMethod === 'bedrock_evaluation' && modelId?.startsWith('global.')) {
      const validModels = availableModels.filter((m) => !m.id.startsWith('global.'));
      if (validModels.length > 0) {
        setModelId(validModels[0].id);
      } else {
        setModelId('');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scoringMethod, modelId, availableModels]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!datasetId) {
      setError('Please select a dataset');
      return;
    }

    if (!credentialId) {
      setError('Please select a credential');
      return;
    }

    if (!modelId) {
      setError('Please select a model');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const params: RunEvaluationParams = {
        datasetId,
        promptId: promptId || undefined,
        provider: currentCredential?.provider || 'openai',
        modelId,
        credentialId,
        scoringMethod,
        passThreshold,
        runAssertions,
      };

      if (scoringMethod === 'llm_judge' && judgeRubricId) {
        params.judgeRubricId = judgeRubricId;
      }

      if (scoringMethod === 'semantic_similarity') {
        params.similarityThreshold = similarityThreshold;
      }

      if (scoringMethod === 'bedrock_evaluation') {
        params.bedrockTaskType = bedrockTaskType;
        params.bedrockEvaluationMetrics = bedrockMetrics;
        params.bedrockTimeoutMinutes = bedrockTimeoutMinutes;
      }

      if (calculateSecondaryMetrics && secondaryMetrics.length > 0) {
        params.calculateSecondaryMetrics = true;
        params.secondaryMetrics = secondaryMetrics;
      }

      if (includeSafety) {
        params.includeSafety = true;
        params.safetyFailThreshold = safetyFailThreshold;
      }

      const result = await runEvaluation(params);

      if (onSuccess) {
        onSuccess(result);
      }
      onClose();
    } catch (err) {
      console.error('Error running evaluation:', err);
      setError(err instanceof Error ? err.message : 'Failed to start evaluation');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedDataset = datasets.find((d) => d.id === datasetId);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onClose} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClose(); }} role="presentation" />

        {/* Modal */}
        <div className="relative w-full max-w-2xl bg-white dark:bg-gray-800 rounded-xl shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <FiPlay className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Run Evaluation</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Configure and run an evaluation against a dataset
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit}>
            <div className="p-6 space-y-6 max-h-[60vh] overflow-y-auto">
              {error && (
                <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-3">
                  <FiAlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-red-800 dark:text-red-300 text-sm">{error}</p>
                </div>
              )}

              {/* How Evaluations Work - Collapsible Help */}
              <details className="bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                <summary className="px-4 py-3 font-medium text-gray-900 dark:text-white cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg flex items-center gap-2">
                  <FiHelpCircle className="w-4 h-4 text-blue-500" />
                  How do evaluations work?
                </summary>
                <div className="px-4 pb-4 text-sm text-gray-600 dark:text-gray-400 space-y-2">
                  <p>
                    <strong>1. Dataset Selection:</strong> Choose a dataset with test cases, Q&A pairs,
                    or documents.
                  </p>
                  <p>
                    <strong>2. Prompt Template (Optional):</strong> If selected, variables like{' '}
                    {'{{question}}'} are replaced with dataset values.
                  </p>
                  <p>
                    <strong>3. LLM Execution:</strong> Each item is sent to the LLM and a response is
                    generated.
                  </p>
                  <p>
                    <strong>4. Scoring:</strong> The response is compared against expected output using
                    your chosen scoring method.
                  </p>
                  <p>
                    <strong>5. Results:</strong> Pass/fail status, scores, and detailed metrics are
                    recorded.
                  </p>
                </div>
              </details>

              {/* Dataset Selection */}
              <div>
                <label htmlFor="dataset" className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <FiDatabase className="w-4 h-4" />
                  Dataset <span className="text-red-500">*</span>
                </label>
                <select id="dataset"
                  value={datasetId}
                  onChange={(e) => setDatasetId(e.target.value)}
                  className="w-full px-4 py-2.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={loading}
                  required
                >
                  <option value="">Select a dataset...</option>
                  {datasets.map((ds) => {
                    const contentType = ds.content_type || 'test_cases';
                    const itemCount = ds.item_count ?? ds.test_case_count ?? 0;
                    const typeLabels: Record<string, string> = {
                      test_cases: 'Test Cases',
                      documents: 'Documents',
                      qa_pairs: 'Q&A Pairs',
                      mixed: 'Mixed',
                    };
                    const typeLabel = typeLabels[contentType] || contentType;
                    return (
                      <option key={ds.id} value={ds.id}>
                        {ds.name} ({itemCount} items) [{typeLabel}]
                      </option>
                    );
                  })}
                </select>
                {selectedDataset?.content_type === 'documents' && (
                  <div className="mt-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                    <div className="flex items-start gap-2">
                      <FiAlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-amber-800 dark:text-amber-300">
                        <p className="font-medium">Documents Dataset</p>
                        <p className="mt-1 text-xs opacity-80">
                          Document datasets don&apos;t have expected outputs. Use{' '}
                          <strong>Semantic Similarity</strong> or <strong>LLM Judge</strong> scoring
                          methods for best results.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Prompt Selection */}
              <div>
                <label htmlFor="prompt-template" className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <FiFileText className="w-4 h-4" />
                  Prompt Template
                  <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
                    (Optional)
                  </span>
                </label>
                <select id="prompt-template"
                  value={promptId}
                  onChange={(e) => setPromptId(e.target.value)}
                  className="w-full px-4 py-2.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={loading}
                >
                  <option value="">No prompt - use test case input directly</option>
                  {prompts.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} (v{p.version})
                    </option>
                  ))}
                </select>
                <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <div className="flex items-start gap-2">
                    <FiHelpCircle className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-blue-800 dark:text-blue-300">
                      <p className="font-medium">When to use a prompt template?</p>
                      <ul className="mt-1 text-xs space-y-1 opacity-80">
                        <li>
                          <strong>With prompt:</strong> Variables like {'{{question}}'} are replaced with
                          dataset values before sending to the LLM.
                        </li>
                        <li>
                          <strong>Without prompt:</strong> The dataset&apos;s input_variables are sent
                          directly to the LLM as-is.
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Variable Mismatch Warning */}
              {variableWarning && (
                <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                  <div className="flex items-start gap-3">
                    <FiAlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-yellow-800 dark:text-yellow-300 mb-2">
                        Variable Mismatch
                      </p>
                      {variableWarning.missing.length > 0 && (
                        <p className="text-yellow-700 dark:text-yellow-400 mb-1">
                          <span className="font-medium">Missing in dataset:</span>{' '}
                          <code className="bg-yellow-100 dark:bg-yellow-800/50 px-1 rounded">
                            {variableWarning.missing.join(', ')}
                          </code>
                          <span className="block text-xs mt-0.5 opacity-75">
                            These will appear as {'{{variable}}'} in the output
                          </span>
                        </p>
                      )}
                      {variableWarning.extra.length > 0 && (
                        <p className="text-yellow-700 dark:text-yellow-400">
                          <span className="font-medium">Extra in dataset:</span>{' '}
                          <code className="bg-yellow-100 dark:bg-yellow-800/50 px-1 rounded">
                            {variableWarning.extra.join(', ')}
                          </code>
                          <span className="block text-xs mt-0.5 opacity-75">
                            These won&apos;t be used by the prompt
                          </span>
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Credential Selection */}
              <div>
                <span className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <FiKey className="w-4 h-4" />
                  LLM Provider <span className="text-red-500">*</span>
                </span>
                {credentials.length === 0 ? (
                  <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                    <p className="text-yellow-800 dark:text-yellow-300 text-sm">
                      No LLM providers configured. Please add credentials in{' '}
                      <a href="/settings/llm-providers" className="underline font-medium">
                        Settings &rarr; LLM Providers
                      </a>
                    </p>
                  </div>
                ) : (
                  <select
                    value={credentialId}
                    onChange={(e) => handleCredentialChange(e.target.value)}
                    className="w-full px-4 py-2.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={loading}
                    required
                  >
                    <option value="">Select a provider...</option>
                    {credentials.map((cred) => (
                      <option key={cred.id} value={cred.id}>
                        {cred.name} ({cred.provider})
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Model Selection */}
              {credentialId && (
                <div>
                  <label htmlFor="model" className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <FiCpu className="w-4 h-4" />
                    Model <span className="text-red-500">*</span>
                  </label>
                  <select id="model"
                    value={modelId}
                    onChange={(e) => setModelId(e.target.value)}
                    className="w-full px-4 py-2.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select a model...</option>
                    {filteredModels.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                  </select>

                  {scoringMethod === 'bedrock_evaluation' &&
                    filteredModels.length < availableModels.length && (
                      <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                        Cross-region inference profiles (global.*) are not supported for Bedrock
                        Evaluations.
                        {filteredModels.length === 0 && ' No compatible models available.'}
                      </p>
                    )}

                  {availableModels.length === 0 && (
                    <div className="mt-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                      <div className="flex items-start gap-2">
                        <FiHelpCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                        <div className="text-sm text-amber-800 dark:text-amber-300">
                          <p className="font-medium">No models found</p>
                          <p className="mt-1 text-xs opacity-80">
                            Check your API credentials or provider configuration in Settings.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {availableModels.length > 0 && currentCredential?.provider === 'bedrock' && (
                    <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                      <div className="flex items-start gap-2">
                        <FiHelpCircle className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                        <div className="text-sm text-blue-800 dark:text-blue-300">
                          <p className="font-medium">Don&apos;t see your model?</p>
                          <ul className="mt-1 text-xs opacity-80 space-y-1 list-disc list-inside">
                            <li>
                              <strong>Claude 4.x models:</strong> Add{' '}
                              <code className="bg-blue-100 dark:bg-blue-800/50 px-1 rounded">
                                bedrock:ListInferenceProfiles
                              </code>{' '}
                              permission to your IAM policy
                            </li>
                            <li>
                              <strong>Other models:</strong> Enable ON_DEMAND access in{' '}
                              <a
                                href="https://console.aws.amazon.com/bedrock/home#/modelaccess"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-0.5 underline hover:no-underline"
                              >
                                AWS Bedrock Console <FiExternalLink className="w-3 h-3" />
                              </a>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {availableModels.length > 0 && currentCredential?.provider === 'openai' && (
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Showing models available for your OpenAI API key.
                    </p>
                  )}

                  {availableModels.length > 0 && currentCredential?.provider === 'anthropic' && (
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Showing models available for your Anthropic API key.
                    </p>
                  )}
                </div>
              )}

              {/* Scoring Method */}
              <div>
                <label htmlFor="scoring-method" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                  Scoring Method
                </label>
                <select id="scoring-method"
                  value={scoringMethod}
                  onChange={(e) => setScoringMethod(e.target.value)}
                  className="w-full px-4 py-2.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {availableScoringMethods.map((method) => (
                    <option key={method.value} value={method.value}>
                      {method.label}
                    </option>
                  ))}
                </select>
                <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-sm text-blue-800 dark:text-blue-200">
                  {scoringMethod === 'exact_match' && (
                    <div>
                      <span className="font-medium">Exact Match:</span> Response must exactly match
                      expected output (case-insensitive).{' '}
                      <span className="block mt-1 text-xs opacity-75">
                        Best for: fixed responses, codes, IDs, structured data.
                      </span>
                    </div>
                  )}
                  {scoringMethod === 'contains' && (
                    <div>
                      <span className="font-medium">Contains:</span> Expected output must appear
                      somewhere in the response.{' '}
                      <span className="block mt-1 text-xs opacity-75">
                        Best for: keyword checking, partial matches, flexible responses.
                      </span>
                    </div>
                  )}
                  {scoringMethod === 'similarity' && (
                    <div>
                      <span className="font-medium">Similarity Score:</span> Uses Levenshtein distance
                      to compare strings (0-1 score).{' '}
                      <span className="block mt-1 text-xs opacity-75">
                        Best for: typo tolerance, near-matches, approximate matching.
                      </span>
                    </div>
                  )}
                  {scoringMethod === 'semantic_similarity' && (
                    <div>
                      <span className="font-medium">Semantic Similarity:</span> Uses AI embeddings to
                      compare meaning, not exact words.{' '}
                      <span className="block mt-1 text-xs opacity-75">
                        Best for: checking if responses convey the same information using different
                        wording.
                      </span>
                    </div>
                  )}
                  {scoringMethod === 'llm_judge' && (
                    <div>
                      <span className="font-medium">LLM Judge:</span> AI evaluator scores response
                      quality using configurable rubrics.{' '}
                      <span className="block mt-1 text-xs opacity-75">
                        Best for: complex quality assessment, multi-criteria evaluation, subjective
                        scoring.
                      </span>
                    </div>
                  )}
                  {scoringMethod === 'bedrock_evaluation' && (
                    <div className="bg-amber-50 dark:bg-amber-900/30 -m-3 p-3 rounded-lg border-amber-200 dark:border-amber-700">
                      <div className="flex items-start gap-2">
                        <FiClock className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="font-medium text-amber-800 dark:text-amber-200">
                            AWS Bedrock Evaluation:
                          </span>
                          <span className="text-amber-700 dark:text-amber-300">
                            {' '}
                            Enterprise-grade evaluation using AWS managed infrastructure.
                          </span>
                          <span className="block mt-1 text-xs text-amber-600 dark:text-amber-400">
                            <strong>Note:</strong> This is an asynchronous evaluation that typically
                            takes 5-30 minutes. You can navigate away and check results later.
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* LLM Judge Rubric Selection */}
              {scoringMethod === 'llm_judge' && (
                <div>
                  <label htmlFor="judge-rubric" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                    Judge Rubric
                  </label>
                  <select id="judge-rubric"
                    value={judgeRubricId}
                    onChange={(e) => setJudgeRubricId(e.target.value)}
                    className="w-full px-4 py-2.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select a rubric...</option>
                    {rubrics.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Semantic Similarity Threshold */}
              {scoringMethod === 'semantic_similarity' && (
                <div>
                  <label htmlFor="similarity-threshold" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                    Similarity Threshold: {(similarityThreshold * 100).toFixed(0)}%
                  </label>
                  <input id="similarity-threshold"
                    type="range"
                    min="0"
                    max="100"
                    value={similarityThreshold * 100}
                    onChange={(e) => setSimilarityThreshold(Number(e.target.value) / 100)}
                    className="w-full"
                  />
                </div>
              )}

              {/* AWS Bedrock Evaluation Configuration */}
              {scoringMethod === 'bedrock_evaluation' && (
                <div className="space-y-4 p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
                  <div>
                    <label htmlFor="task-type" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                      Task Type
                    </label>
                    <select id="task-type"
                      value={bedrockTaskType}
                      onChange={(e) => setBedrockTaskType(e.target.value)}
                      className="w-full px-4 py-2.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    >
                      {BEDROCK_TASK_TYPES.map((taskType) => (
                        <option key={taskType.value} value={taskType.value}>
                          {taskType.label} - {taskType.description}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Select the task type that best matches your evaluation scenario
                    </p>
                  </div>

                  <div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                      Evaluation Metrics
                    </span>
                    <div className="space-y-2">
                      {BEDROCK_EVALUATION_METRICS.map((metric) => (
                        <label aria-label={metric.label}
                          key={metric.value}
                          className="flex items-start gap-2 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={bedrockMetrics.includes(metric.value)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setBedrockMetrics([...bedrockMetrics, metric.value]);
                              } else if (bedrockMetrics.length > 1) {
                                setBedrockMetrics(bedrockMetrics.filter((m) => m !== metric.value));
                              }
                            }}
                            className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500 mt-0.5"
                          />
                          <div>
                            <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                              {metric.label}
                            </span>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {metric.description}
                            </p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Timeout Configuration */}
                  <div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                      <FiClock className="inline w-4 h-4 mr-1" />
                      Timeout (minutes)
                    </span>
                    <div className="flex items-center gap-3">
                      <input
                        type="number"
                        min={15}
                        max={180}
                        value={bedrockTimeoutMinutes}
                        onChange={(e) =>
                          setBedrockTimeoutMinutes(Number.parseInt(e.target.value) || 60)
                        }
                        className="w-24 px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                      <span className="text-sm text-gray-500 dark:text-gray-400">(15-180 minutes)</span>
                    </div>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Job will be marked as failed if not completed within this time. Most evaluations
                      complete within 30 minutes.
                    </p>
                  </div>

                  <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-md text-sm">
                    <p className="text-blue-800 dark:text-blue-200">
                      <strong>Accuracy</strong> and <strong>Robustness</strong> metrics use BERTScore
                      internally for semantic comparison.
                    </p>
                  </div>
                </div>
              )}

              {/* Pass Threshold */}
              <div>
                <label htmlFor="pass-threshold" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                  Pass Threshold: {(passThreshold * 100).toFixed(0)}%
                </label>
                <input id="pass-threshold"
                  type="range"
                  min="0"
                  max="100"
                  value={passThreshold * 100}
                  onChange={(e) => setPassThreshold(Number(e.target.value) / 100)}
                  className="w-full"
                />
              </div>

              {/* Advanced Options Toggle */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                >
                  {showAdvanced ? '- Hide' : '+ Show'} Advanced Options
                </button>
              </div>

              {/* Advanced Options */}
              {showAdvanced && (
                <div className="space-y-4 pl-4 border-l-2 border-gray-200 dark:border-gray-700">
                  {/* Run Assertions */}
                  <span className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={runAssertions}
                      onChange={(e) => setRunAssertions(e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Run Assertions
                      </span>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Execute assertions defined in test cases
                      </p>
                    </div>
                  </span>

                  {/* Safety Testing */}
                  <div className="space-y-3">
                    <span className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={includeSafety}
                        onChange={(e) => setIncludeSafety(e.target.checked)}
                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                      />
                      <div className="flex items-center gap-2">
                        <FiShield className="w-4 h-4 text-orange-500" />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Include Safety Tests
                        </span>
                      </div>
                    </span>

                    {includeSafety && (
                      <div className="ml-7">
                        <label htmlFor="fail-threshold" className="text-sm text-gray-600 dark:text-gray-400 mb-1 block">
                          Fail Threshold
                        </label>
                        <select id="fail-threshold"
                          value={safetyFailThreshold}
                          onChange={(e) => setSafetyFailThreshold(e.target.value)}
                          className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white"
                        >
                          {SAFETY_FAIL_THRESHOLDS.map((t) => (
                            <option key={t.value} value={t.value}>
                              {t.label} - {t.description}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>

                  {/* Secondary Metrics */}
                  <div className="space-y-3">
                    <span className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={calculateSecondaryMetrics}
                        onChange={(e) => {
                          setCalculateSecondaryMetrics(e.target.checked);
                          if (!e.target.checked) setSecondaryMetrics([]);
                        }}
                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Calculate Secondary Metrics
                        </span>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Additional metrics calculated alongside the primary scoring method
                        </p>
                      </div>
                    </span>

                    {calculateSecondaryMetrics && (
                      <div className="ml-7 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg space-y-2">
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                          Select additional metrics to calculate (won&apos;t affect pass/fail):
                        </p>
                        {scoringMethod !== 'semantic_similarity' && (
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={secondaryMetrics.includes('semantic_similarity')}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSecondaryMetrics([...secondaryMetrics, 'semantic_similarity']);
                                } else {
                                  setSecondaryMetrics(
                                    secondaryMetrics.filter((m) => m !== 'semantic_similarity')
                                  );
                                }
                              }}
                              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                              Semantic Similarity
                            </span>
                          </label>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 rounded-b-xl">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || !datasetId || !credentialId || !modelId}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Starting...
                  </>
                ) : (
                  <>
                    <FiPlay className="w-4 h-4" />
                    Run Evaluation
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RunEvaluationModal;
